import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import Icon from './Icon';
import { CardLoader, Error, TextButton } from '../components';
import UploadPictureModal from './formBuilder/formElements/UploadPictureModal';
import Tooltip from './Tooltip';
import classNames from '../utility/classNames';
import { useParams } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { useProperty } from '../hooks/useProperty';
import { PROPERTIES, USERS, IMAGES } from '../../firebase';
import { db } from '../../firebase/clientApp';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { collection, query, orderBy } from 'firebase/firestore';
import { FaStar, FaRegStar } from 'react-icons/fa';

//tbd = clean up

export default function PictureGallery({
  //users were previously able to upload a logo, leaving this here in case we need to re-enable it
  isLogo = false,
}) {
  const currentUser = useUser();
  const { propertyId, uid } = useParams();
  const { propertyData } = useProperty();

  const firestorePath = `${USERS}/${
    uid || currentUser?.user?.uid
  }/${PROPERTIES}/${propertyId}/${IMAGES}`;

  const filePath = `images/${uid || currentUser?.user?.uid}/${propertyId}`;

  const imagesRef = query(collection(db, firestorePath), orderBy('createdAt'));

  const [dbImages, loading, error] = useCollectionData(imagesRef, {
    snapshotListenOptions: { includeMetadataChanges: true },
  });

  const [images, setImages] = useState([]);

  useEffect(() => {
    if (dbImages) {
      const imageData = [];
      dbImages.forEach((image) => {
        imageData.push({
          id: image.id,
          url: image.photoUrlBE,
        });
      });
      setImages(imageData);
    }
  }, [dbImages]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [renderButton, setRenderButton] = useState(false);
  const [coverImage, setCoverImage] = useState('');
  const [direction, setDirection] = useState(null);
  const [loaders, setLoaders] = useState({});
  const { pathname } = useLocation();
  const sharing = pathname.includes('sharing');

  useEffect(() => {
    if (!propertyData) return;
    setCoverImage(propertyData.coverImageId);
  }, [propertyData]);

  useEffect(() => {
    if (images?.length > 1) {
      setRenderButton(true);
    } else {
      setRenderButton(false);
    }
  }, [images]);

  // Slider
  const maxSlide = images?.length - 1;
  const nextSlide = () => {
    setDirection('right');
    if (currentSlide === maxSlide) return setCurrentSlide(0);
    return setCurrentSlide(currentSlide + 1);
  };
  const prevSlide = () => {
    setDirection('left');
    if (currentSlide === 0) return setCurrentSlide(maxSlide || 0);
    return setCurrentSlide(currentSlide - 1);
  };

  let currentSlideId = null;
  let newData = images;

  if (!isLogo) {
    // Change order of images
    const copiedImages = [...images];

    // extract the cover image and put it at first
    const index = copiedImages.findIndex((image) => image.id === coverImage);
    const data = copiedImages.splice(index, 1);
    newData = data && copiedImages && [...data, ...copiedImages];

    currentSlideId = newData && newData[currentSlide]?.id;
  }

  const handlePictureDelete = async () => {
    const functions = getFunctions();
    const deleteImage = httpsCallable(functions, 'onDeleteImage');
    const deleteFields = httpsCallable(functions, 'onDeleteFields');
    setLoaders({ ...loaders, delete: true });

    try {
      await (isLogo
        ? deleteFields({
            firestorePath,
            filePath,
            fields: ['photoUrlBE', 'photoName'],
          })
        : deleteImage({ propertyId: propertyId, imageId: currentSlideId }));

      isLogo && setImages(null);
      setLoaders({ ...loaders, delete: false });

      toast.success('Picture deleted!', { position: 'top-right' });
      if (currentSlide !== 0) setCurrentSlide(currentSlide - 1);
    } catch (err) {
      setLoaders({ ...loaders, delete: false });
      console.error(err);
      toast.error('Error deleting picture', { position: 'top-right' });
    }
  };

  const handleSelectCoverImage = () => {
    const functions = getFunctions();
    const changeCoverImage = httpsCallable(functions, 'onChangeCoverImage');
    setLoaders({ ...loaders, coverImage: true });

    changeCoverImage({ propertyId, imageId: currentSlideId })
      .then(() => {
        setDirection(null);
        setLoaders({ ...loaders, coverImage: false });
        toast.success('Cover image updated');
        setCoverImage(currentSlideId);
        setCurrentSlide(0);
      })
      .catch((err) => {
        console.error(err);
        setLoaders({ ...loaders, coverImage: false });
        toast.error('Failed to update cover image');
      });
  };

  const coverImageButtonText = (
    <div className="flex flex-row items-center gap-1">
      {coverImage === currentSlideId ? (
        <FaStar className="text-primary text-3xl" />
      ) : (
        <FaRegStar className="text-neutral-500 hover:text-primary-500 text-3xl" />
      )}
    </div>
  );

  const changeSlideIcon = (icon) => {
    return images.length > 1 ? (
      <button
        type="button"
        className={classNames(
          'absolute inline-flex bg-white shadow-sm items-center p-[2px] border border-transparent rounded-full text-neutral-700 hover:text-primary-500 top-1/3',
          !renderButton && 'cursor-default',
          icon === 'left' ? '-left-3' : '-right-3'
        )}
        onClick={icon === 'left' ? prevSlide : nextSlide}
        disabled={!renderButton}
      >
        {images.length > 1 && icon === 'left' ? (
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        ) : images.length > 1 && icon === 'right' ? (
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        ) : (
          <span className="h-6 w-6">&nbsp;</span>
        )}
      </button>
    ) : null;
  };

  const renderImages = () => {
    if (loading) {
      return <CardLoader />;
    }

    if (error) {
      return <Error error={error} />;
    }

    return (
      <>
        <div className="w-full flex">
          <div className="flex-1 group relative">
            <div
              className={classNames(
                'overflow-hidden object-contain max-w-[600px] -z-10',
                sharing
                  ? 'max-h-[350px] min-h-[340px] rounded-lg'
                  : 'max-h-[270px] min-h-[265px]',
                !newData?.length && 'flex justify-center items-center'
              )}
            >
              {newData?.length ? (
                newData.map((slide, index) => {
                  return (
                    <Fragment key={index}>
                      <img
                        src={slide.url}
                        alt={isLogo ? 'Company Logo' : 'Property image'}
                        className={classNames(
                          index === currentSlide
                            ? 'block max-w-full max-h-full  w-full object-fill rounded-lg'
                            : 'hidden',
                          !sharing && 'rounded-b-none',
                          direction === 'right'
                            ? 'animate-enter-right'
                            : direction === 'left'
                            ? 'animate-enter-left'
                            : ''
                        )}
                      />
                    </Fragment>
                  );
                })
              ) : (
                <div className="p-8 flex items-center justify-center">
                  <p className="text-md font-normal text-center text-neutral-900">
                    {!sharing
                      ? 'You can add pictures for your property by pressing the plus icon.'
                      : 'No images to show'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        {!isLogo && (
          <>
            {changeSlideIcon('left')}

            {changeSlideIcon('right')}
          </>
        )}

        {!sharing && (
          <div
            className={classNames(
              'grid grid-cols-3 justify-between item-center h-full'
            )}
          >
            <UploadPictureModal
              firestorePath={firestorePath}
              filePath={filePath}
              propertyId={propertyId}
              type={isLogo ? 'logo' : 'gallery'}
            />
            <div className="flex justify-center items-center">
              {newData?.length > 0 && (
                <span className="inline-flex items-center p-3 text-neutral-600">{`${
                  currentSlide + 1
                } / ${newData.length}`}</span>
              )}
            </div>
            <div className="flex flex-row justify-end items-center">
              {images?.length ? (
                <>
                  {!isLogo && (
                    <Tooltip
                      text={
                        coverImage === currentSlideId
                          ? 'This is your current cover image'
                          : 'Click to set this image as your cover image'
                      }
                      classes="text-neutral-900"
                      offset="-top-12 left-1"
                    >
                      <div className="flex p-2 justify-center">
                        <TextButton
                          classes="text-primary text-md focus:ring-primary focus:ring-2 focus:ring-offset-0 transition-all"
                          style="table"
                          disabled={coverImage === currentSlideId}
                          loading={loaders.coverImage}
                          srText="Set as cover image"
                          text={coverImageButtonText}
                          handleClick={() => handleSelectCoverImage()}
                        />
                      </div>
                    </Tooltip>
                  )}
                  <Tooltip
                    text="Delete image"
                    classes="text-neutral-900"
                    offset="-top-10 -left-16"
                  >
                    <button
                      type="button"
                      onClick={handlePictureDelete}
                      className="transition inline-flex border-2 border-transparent justify-center items-center p-2 rounded-md outline-none text-base font-medium text-primary hover:text-primary-500  disabled:opacity-50 disabled:cursor-not-allowed peer"
                    >
                      <span className="sr-only">Delete</span>
                      {!loaders.delete ? (
                        <Icon
                          icon="FiTrash"
                          classes="text-3xl"
                          aria-hidden="true"
                        />
                      ) : (
                        <Icon
                          icon="BiLoaderAlt"
                          className="text-2xl animate-spin"
                        ></Icon>
                      )}
                    </button>
                  </Tooltip>
                </>
              ) : null}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <section
      aria-labelledby="property-gallery"
      className="w-full flex justify-center"
    >
      <div
        className={classNames(
          ' flex flex-col lg:px-0  max-h-[350px] w-full rounded-lg bg-white max-w-[600px] relative'
        )}
      >
        {renderImages()}
      </div>
    </section>
  );
}
